import { useCallback, useEffect } from "react";
import { startOfDay } from "date-fns";
import { SubscriptionData } from "../../api/useWebSockets";
import { getRecords } from "../../api/useRecords";
import { useUser } from "../../api/useAuth";
import { getStore } from "../../store/store";
import { isKioskCallbackRole } from "../../utils/constants";
import { KIOSK_CALLBACK_TASK_KEY_PREFIX, buildKioskCallbackItem } from "./utils";
import { KioskCallbackTask } from "./types";

export const KIOSK_CALLBACK_SUBSCRIPTION_UID = "kiosk_callback_tasks";

const KIOSK_CALLBACK_FIELDS = "id,content,date_created,hidden_field_automations,related_with.collection,related_with.item";

const addKioskCallbackTasks = (tasks: Array<KioskCallbackTask>) => {
    tasks.forEach((task) => getStore().assistantDialog.addItem(buildKioskCallbackItem(task)));
};

const fetchKioskCallbackTask = async (taskId: number) => {
    const { items: tasks } = await getRecords("tasks", KIOSK_CALLBACK_FIELDS, {
        extraOptions: { filter: { id: { _eq: taskId } } },
    });

    addKioskCallbackTasks(tasks);
};

const fetchTodayOpenKioskCallbackTasks = async () => {
    const { items: tasks } = await getRecords("tasks", KIOSK_CALLBACK_FIELDS, {
        extraOptions: {
            filter: {
                _and: [
                    { status: { _eq: "open" } },
                    { hidden_field_automations: { _starts_with: KIOSK_CALLBACK_TASK_KEY_PREFIX } },
                    { date_created: { _gte: startOfDay(new Date()).toISOString() } },
                ],
            },
        },
    });

    addKioskCallbackTasks(tasks);
};

export const useKioskCallbackAssistant = () => {
    const { data: currentUser } = useUser();
    const isCallbackRecipient = isKioskCallbackRole(currentUser?.role?.id);

    const handleKioskCallbackEvent = useCallback(
        (subscriptionData: SubscriptionData) => {
            const { event, data } = subscriptionData;

            if (!isCallbackRecipient || event !== "create" || !Array.isArray(data)) return;

            data.forEach((task: { id: number }) => fetchKioskCallbackTask(task.id));
        },
        [isCallbackRecipient]
    );

    useEffect(() => {
        if (!isCallbackRecipient) return;

        fetchTodayOpenKioskCallbackTasks();
    }, [isCallbackRecipient]);

    return { handleKioskCallbackEvent };
};
